import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from '../../services/enterpriseApi';
import Card from '../../components/common/Card';
import { LoadingScreen } from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import {
  FileText,
  Brain,
  TrendingUp,
  Download,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';

export default function AuditTrailViewer() {
  const [selectedDecision, setSelectedDecision] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    decision_type: '',
    model_used: '',
    min_confidence: 0,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: () => auditApi.getStats(),
  });

  const { data: decisions, isLoading: decisionsLoading } = useQuery({
    queryKey: ['audit-decisions', filters],
    queryFn: () => auditApi.getAllDecisions({ limit: 20, ...filters }),
  });

  const { data: reasoningTree } = useQuery({
    queryKey: ['reasoning-tree', selectedDecision],
    queryFn: () => auditApi.getReasoningTree(selectedDecision!),
    enabled: !!selectedDecision,
  });

  const handleExport = async () => {
    const endDate = new Date().toISOString();
    const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    
    try {
      await auditApi.exportAudit({
        start_date: startDate,
        end_date: endDate,
        format: 'json',
      });
      alert('Audit trail exported successfully');
    } catch (error) {
      alert('Failed to export audit trail');
    }
  };

  if (statsLoading || decisionsLoading) {
    return <LoadingScreen message="Loading audit trail..." />;
  }

  const statCards = [
    {
      title: 'Total Decisions',
      value: stats?.data?.totalDecisions || 0,
      icon: Brain,
      color: 'bg-primary-500',
    },
    {
      title: 'Avg Confidence',
      value: `${((stats?.data?.averageConfidence || 0) * 100).toFixed(1)}%`,
      icon: TrendingUp,
      color: 'bg-green-500',
    },
    {
      title: 'Models Used',
      value: Object.keys(stats?.data?.byModel || {}).length,
      icon: FileText,
      color: 'bg-secondary-500',
    },
    {
      title: 'Recent (last 10)',
      value: stats?.data?.recentDecisions?.length || 0,
      icon: Clock,
      color: 'bg-accent-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
            AI Decision Audit Trail
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Complete reasoning trail for compliance and transparency
          </p>
        </div>
        <Button onClick={handleExport} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export Audit
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={filters.decision_type}
            onChange={(e) => setFilters({ ...filters, decision_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Decision Types</option>
            <option value="image_analysis">Image Analysis</option>
            <option value="nlq">Natural Language Query</option>
            <option value="temporal_analysis">Temporal Analysis</option>
            <option value="suitability_analysis">Suitability Analysis</option>
          </select>

          <select
            value={filters.model_used}
            onChange={(e) => setFilters({ ...filters, model_used: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Models</option>
            <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
            <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
            <option value="gpt-4">GPT-4</option>
            <option value="claude-3">Claude 3</option>
          </select>

          <input
            type="range"
            min="0"
            max="100"
            value={filters.min_confidence * 100}
            onChange={(e) =>
              setFilters({ ...filters, min_confidence: parseInt(e.target.value) / 100 })
            }
            className="flex-1"
          />
          <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[100px]">
            Min Confidence: {(filters.min_confidence * 100).toFixed(0)}%
          </span>
        </div>
      </Card>

      {/* Decisions List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Decision List */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Decision Log</h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {decisions?.data && (Array.isArray(decisions.data) ? decisions.data : decisions.data?.decisions ?? []).length > 0 ? (
              (Array.isArray(decisions.data) ? decisions.data : decisions.data?.decisions ?? []).map((decision: any) => (
                <button
                  key={decision.id}
                  onClick={() => setSelectedDecision(decision.id)}
                  className={`w-full text-left p-4 border rounded-lg transition-colors ${
                    selectedDecision === decision.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-primary-600" />
                      <span className="font-semibold text-gray-900 dark:text-white text-sm">
                        {decision.decision_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        decision.confidence_score >= 0.85
                          ? 'bg-green-100 text-green-800'
                          : decision.confidence_score >= 0.7
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {(decision.confidence_score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                    {decision.prompt_used ?? decision.prompt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{decision.ai_model ?? decision.model_used}</span>
                    <span>{new Date(decision.created_at).toLocaleString()}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300">No decisions found</p>
              </div>
            )}
          </div>
        </Card>

        {/* Right: Reasoning Tree */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Reasoning Tree</h2>
          {selectedDecision && reasoningTree?.data ? (
            <div className="space-y-4">
              {/* Decision Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Decision Context</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Model:</span>{' '}
                    <span className="text-gray-600 dark:text-gray-400">{reasoningTree.data.model_used}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Confidence:</span>{' '}
                    <span className="text-gray-600 dark:text-gray-400">
                      {(reasoningTree.data.confidence_score * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Timestamp:</span>{' '}
                    <span className="text-gray-600 dark:text-gray-400">
                      {new Date(reasoningTree.data.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Reasoning Steps */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Reasoning Steps</h3>
                <div className="space-y-3">
                  {reasoningTree.data.reasoning_steps?.map((step: any, index: number) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-sm">
                          {step.step}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white mb-1">{step.description}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-500 h-2 rounded-full"
                              style={{ width: `${step.confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {(step.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Result */}
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <h3 className="font-semibold text-primary-900 mb-2">Final Result</h3>
                <pre className="text-sm text-primary-800 whitespace-pre-wrap">
                  {JSON.stringify(reasoningTree.data.result, null, 2)}
                </pre>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">Select a decision to view reasoning tree</p>
            </div>
          )}
        </Card>
      </div>

      {/* Compliance Info */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Audit Trail Compliance
        </h3>
        <div className="prose prose-sm max-w-none text-gray-600 dark:text-gray-300">
          <p>
            The AI Decision Audit Trail provides complete transparency for regulatory compliance:
          </p>
          <ul className="space-y-2 mt-3">
            <li>
              <strong>Step-by-step reasoning:</strong> Every AI decision is logged with its
              complete reasoning process
            </li>
            <li>
              <strong>Prompt & context capture:</strong> All inputs and environmental context
              are recorded
            </li>
            <li>
              <strong>Confidence tracking:</strong> Each step includes confidence scores for
              validation
            </li>
            <li>
              <strong>Model attribution:</strong> Clear identification of which AI model was used
            </li>
            <li>
              <strong>Immutable records:</strong> All decisions are cryptographically sealed
              (see Integrity Dashboard)
            </li>
            <li>
              <strong>Export capability:</strong> Generate compliance reports in JSON/CSV format
            </li>
          </ul>
        </div>
      </Card>
    </div>
  );
}

// Made with Bob
